<script setup lang="ts">
  // "Cierre del día" page: plain-language alert banners, a "Ventas netas
  // del día" hero (with a "Cobrado" collections legend) and a delta vs the
  // same weekday last week, and reordered report cards (sales → payments →
  // caja → top sellers → hourly → club) with a collapsed tech-detail card
  // for the accounting jargon. Today renders a
  // live preview; past days read stored snapshots with version history,
  // staleness banner and regeneration. Schema-1 snapshots lack the v2
  // fields (comparisons/channels/topProducts/hourly) — everything
  // null-guards.
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from 'vue';
  import DayClosesService from '@/api/services/day-closes.service';
  import StatsService from '@/api/services/stats.service';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import V2ReportPage from '@/components/v2/stats/V2ReportPage.vue';
  import {
    IDayCloseReportAlert,
    IDayCloseReportRow,
    IDayCloseReportSection,
  } from '@/components/v2/stats/day-close/day-close-report.types';
  import V2DayCloseAlerts from '@/components/v2/stats/day-close/V2DayCloseAlerts.vue';
  import V2DayCloseCajaCard from '@/components/v2/stats/day-close/V2DayCloseCajaCard.vue';
  import V2DayCloseHero from '@/components/v2/stats/day-close/V2DayCloseHero.vue';
  import V2DayCloseHourlyChart from '@/components/v2/stats/day-close/V2DayCloseHourlyChart.vue';
  import V2DayCloseSectionCard from '@/components/v2/stats/day-close/V2DayCloseSectionCard.vue';
  import V2DayCloseTechDetail from '@/components/v2/stats/day-close/V2DayCloseTechDetail.vue';
  import { usePrinting } from '@/composition/use-printing';
  import { useUi } from '@/composition/use-ui';
  import {
    getDayCloseCollectedDisplay,
    getDayCloseNetDisplay,
  } from '@/domain/stats/day-close-display';
  import { AlertType } from '@/interfaces/IAlert';
  import {
    DayClosePaymentBucket,
    IDayClose,
    IDayCloseDetailResponse,
    IDayCloseWsPayload,
  } from '@/interfaces/IDayClose';
  import { IStatsContext } from '@/interfaces/IStatsContext';
  import { useSocketStore } from '@/composition/use-socket';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { displayPrice, roundPrice } from '@/utils';

  const ui = useUi();
  const printing = usePrinting();
  const socketStore = useSocketStore();
  const restaurantStore = useRestaurantStore();
  const statsService = new StatsService();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  //#region Date selection + context

  function localToday(): string {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
  }

  const context = ref<IStatsContext | null>(null);
  const selectedDate = ref('');
  const selectedVersion = ref<number | null>(null);

  const currentBusinessDate = computed(
    () => context.value?.currentBusinessDate ?? localToday()
  );

  /** Today (or future guard): always a live computation, never a snapshot. */
  const isLiveDay = computed(
    () => selectedDate.value >= currentBusinessDate.value
  );

  function shiftDate(dateStr: string, days: number): string {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day + days);
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${m}-${d}`;
  }

  function selectDate(dateStr: string) {
    if (!dateStr || dateStr === selectedDate.value) return;
    selectedDate.value =
      dateStr > currentBusinessDate.value ? currentBusinessDate.value : dateStr;
    selectedVersion.value = null;
    syncRouteQuery();
    load();
  }

  function syncRouteQuery() {
    if (proxy.$route.query.date === selectedDate.value) return;
    proxy.$router
      .replace({ query: { ...proxy.$route.query, date: selectedDate.value } })
      .catch(() => undefined);
  }

  // Guards out-of-order context responses on rapid branch switches.
  let contextGeneration = 0;

  async function loadContext() {
    const restaurant = restaurantStore.restaurant;
    const branch = restaurantStore.selectedBranch;
    if (!restaurant || !branch) return;
    const generation = ++contextGeneration;
    try {
      const result = await statsService.getContext(restaurant.id, branch.id);
      if (generation !== contextGeneration) return;
      context.value = result;
    } catch {
      if (generation !== contextGeneration) return;
      // Browser-local fallback: the page still works without the cutoff.
      context.value = null;
    }
  }

  //#endregion

  //#region Report loading

  const detail = ref<IDayCloseDetailResponse | null>(null);
  const loading = ref(false);
  const notFound = ref(false);

  // Guards out-of-order responses on rapid date/branch switches.
  let requestGeneration = 0;

  async function load() {
    const restaurant = restaurantStore.restaurant;
    const branch = restaurantStore.selectedBranch;
    if (!restaurant || !branch || !selectedDate.value) return;

    const generation = ++requestGeneration;
    loading.value = true;
    notFound.value = false;
    try {
      const result = await DayClosesService.get({
        restaurantId: restaurant.id,
        branchId: branch.id,
        businessDate: selectedDate.value,
        live: isLiveDay.value,
        version: selectedVersion.value ?? undefined,
      });
      if (generation !== requestGeneration) return;
      detail.value = result;
    } catch (error) {
      if (generation !== requestGeneration) return;
      detail.value = null;
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404) {
        notFound.value = true;
      } else {
        ui.serverError(error, i18nT('v2Stats.dayClose.loadError'));
      }
    } finally {
      if (generation === requestGeneration) loading.value = false;
    }
  }

  function selectVersion(version: number) {
    if (version === selectedVersion.value) return;
    selectedVersion.value = version;
    load();
  }

  // Vue 2.7 templates don't support TS casts: narrow the event targets here.
  function onDateInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    selectDate(target.value);
  }

  function onVersionSelectChange(event: Event) {
    const target = event.target as HTMLSelectElement;
    selectVersion(Number(target.value));
  }

  const payload = computed<IDayClose | null>(
    () => detail.value?.payload ?? null
  );

  //#endregion

  //#region Actions (regenerate / print)

  const regenerating = ref(false);

  async function regenerate() {
    const restaurant = restaurantStore.restaurant;
    const branch = restaurantStore.selectedBranch;
    if (!restaurant || !branch || regenerating.value) return;

    regenerating.value = true;
    try {
      await DayClosesService.regenerate({
        restaurantId: restaurant.id,
        branchId: branch.id,
        businessDate: selectedDate.value,
      });
      ui.showToast(
        AlertType.Success,
        i18nT('v2Stats.dayClose.regenerateSuccess')
      );
      selectedVersion.value = null;
      await load();
    } catch (error) {
      ui.serverError(error, i18nT('v2Stats.dayClose.regenerateError'));
    } finally {
      regenerating.value = false;
    }
  }

  // First print of a (date, version) in this session is the print; later
  // clicks are watermarked REIMPRESIÓN.
  const printedKeys = new Set<string>();

  async function print() {
    if (!payload.value || !detail.value) return;
    const key = `${selectedDate.value}:${payload.value.header.version}`;
    try {
      await printing.printDayClose(payload.value, {
        live: detail.value.live,
        reprint: !detail.value.live && printedKeys.has(key),
      });
      printedKeys.add(key);
    } catch (error) {
      ui.serverError(error, i18nT('v2Stats.dayClose.printError'));
    }
  }

  //#endregion

  //#region Formatting

  const currencySymbol = computed(
    () => restaurantStore.currency?.symbol || '$'
  );

  function money(value: number | null | undefined): string {
    if (value === null || typeof value === 'undefined') return '—';
    return `${currencySymbol.value}${displayPrice(value, true)}`;
  }

  function formatTime(iso: string | null): string {
    if (!iso) return '–';
    return new Date(iso).toLocaleTimeString(undefined, {
      timeZone: payload.value?.header.timeZone,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function formatDateTime(iso: string | null): string {
    if (!iso) return '—';
    const tz = payload.value?.header.timeZone;
    return new Date(iso).toLocaleString(undefined, {
      timeZone: tz,
      hour12: false,
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function bucketLabel(bucket: DayClosePaymentBucket | string): string {
    return i18nT(`v2Stats.bucket.${bucket}`);
  }

  function discountTypeLabel(type: string): string {
    const known = ['manual', 'coupon', 'rule', 'club'];
    return known.includes(type) ? i18nT(`v2Stats.discountType.${type}`) : type;
  }

  /** Integer share with a 1% floor so tiny non-zero shares stay visible. */
  function barShare(amount: number, total: number): number {
    if (!total || amount <= 0) return 0;
    return Math.min(100, Math.max(1, Math.round((amount / total) * 100)));
  }

  //#endregion

  //#region Alerts + hero + section view models

  const zeroSales = computed(() => payload.value?.kpis.orders === 0);

  const staleSessions = computed(() =>
    (payload.value?.cashSessions ?? []).filter(
      session => session.state === 'open' && session.spansMultipleDays
    )
  );

  const staleSessionRows = computed(() =>
    staleSessions.value.map(session => ({
      key: `stale-${session.cajaId}`,
      label: session.cajaName,
      window: formatDateTime(session.openedAt),
    }))
  );

  const alerts = computed<IDayCloseReportAlert[]>(() => {
    const header = payload.value?.header;
    if (!header) return [];
    const list: IDayCloseReportAlert[] = [];
    if (header.openOrdersCarried > 0) {
      list.push({
        key: 'open-orders',
        danger: false,
        text: i18nT('v2Stats.dayClose.alertOpenOrders', {
          n: header.openOrdersCarried,
        }),
      });
    }
    if (staleSessions.value.length > 0) {
      list.push({
        key: 'stale-sessions',
        danger: false,
        text: i18nT('v2Stats.dayClose.alertStaleSessions', {
          n: staleSessions.value.length,
        }),
      });
    }
    if (header.mixedCurrencies) {
      list.push({
        key: 'mixed-currencies',
        danger: false,
        text: i18nT('v2Stats.dayClose.alertMixedCurrencies', {
          currency: header.currency,
        }),
      });
    }
    if (header.inconsistencies > 0) {
      list.push({
        key: 'inconsistencies',
        danger: true,
        text: i18nT('v2Stats.dayClose.alertInconsistencies', {
          n: header.inconsistencies,
        }),
      });
    }
    return list;
  });

  const CHANNEL_LABEL_KEYS: Record<string, string> = {
    seat: 'v2Stats.dayClose.channelSeat',
    away: 'v2Stats.dayClose.channelAway',
    delivery: 'v2Stats.dayClose.channelDelivery',
  };

  /** Sus ventas: waterfall rows + channel rows with proportion bars. */
  const salesSection = computed<IDayCloseReportSection | null>(() => {
    const p = payload.value;
    if (!p || zeroSales.value) return null;
    const wf = p.waterfall;
    const rows: IDayCloseReportRow[] = [
      {
        key: 'gross',
        label: i18nT('v2Stats.dayClose.sold'),
        value: money(wf.grossSales),
      },
    ];
    if (wf.benefitsTotal !== 0) {
      rows.push({
        key: 'benefits',
        label: i18nT('v2Stats.dayClose.discountsPromos'),
        value: `−${money(wf.benefitsTotal)}`,
      });
      for (const benefit of wf.benefitsByType) {
        rows.push({
          key: `benefit-${benefit.type}`,
          label: `${discountTypeLabel(benefit.type)} (${benefit.count})`,
          value: `−${money(benefit.amount)}`,
          indent: true,
          muted: true,
        });
      }
    }
    // Shared helper: handles the old-API fallback (served netSales still
    // including comps) in one place with the hero and the print.
    const net = getDayCloseNetDisplay(p);
    if (net.nonMonetary !== 0) {
      rows.push({
        key: 'non-monetary',
        label: i18nT('v2Stats.dayClose.compsGroup'),
        value: `−${money(net.nonMonetary)}`,
        muted: true,
        note: i18nT('v2Stats.dayClose.compsGroupNote'),
      });
    }
    rows.push({
      key: 'net-sales',
      label: i18nT('v2Stats.dayClose.netSales'),
      value: money(net.netSales),
      strong: true,
    });
    // Bridge row: comps come back in so the chain to Total del día stays
    // provable (dayTotal re-includes them by identity).
    if (net.nonMonetary !== 0) {
      rows.push({
        key: 'non-monetary-readd',
        label: i18nT('v2Stats.dayClose.compsGroup'),
        value: `+${money(net.nonMonetary)}`,
        muted: true,
        note: i18nT('v2Stats.dayClose.compsReaddNote'),
      });
    }
    if (wf.taxes !== 0) {
      rows.push({
        key: 'taxes',
        label: i18nT('v2Stats.dayClose.taxes'),
        value: `+${money(wf.taxes)}`,
      });
    }
    if (wf.rounding !== 0) {
      rows.push({
        key: 'rounding',
        label: i18nT('v2Stats.dayClose.rounding'),
        value: `${wf.rounding > 0 ? '+' : '−'}${money(Math.abs(wf.rounding))}`,
      });
    }
    rows.push({
      key: 'total',
      label: i18nT('v2Stats.dayClose.dayTotal'),
      value: money(wf.dayTotal),
      strong: true,
    });
    if (wf.tips !== 0) {
      rows.push({
        key: 'tips',
        label: i18nT('v2Stats.dayClose.tipsAside'),
        value: money(wf.tips),
        muted: true,
      });
    }
    for (const channel of p.channels ?? []) {
      rows.push({
        key: `channel-${channel.type}`,
        label: `${i18nT(
          CHANNEL_LABEL_KEYS[channel.type] ?? channel.type
        )} (${channel.orders})`,
        value: money(channel.amount),
        barPct: barShare(channel.amount, wf.dayTotal),
      });
    }
    return {
      key: 'sales',
      title: i18nT('v2Stats.dayClose.sectionSales'),
      rows,
    };
  });

  const MONEY_BUCKETS: DayClosePaymentBucket[] = [
    'cash',
    'card',
    'online',
    'banktransfer',
    'qr',
  ];

  /** Cómo le pagaron: money tenders with bars + grouped saldo/comps rows. */
  const paymentsSection = computed<IDayCloseReportSection | null>(() => {
    const p = payload.value;
    if (!p || zeroSales.value) return null;
    const tenders = p.tenders;
    const rows: IDayCloseReportRow[] = [];
    let balanceTotal = 0;
    for (const tender of tenders.rows) {
      if (!MONEY_BUCKETS.includes(tender.bucket)) {
        if (tender.balancePayment) {
          balanceTotal = roundPrice(balanceTotal + tender.amount);
        }
        continue;
      }
      if (tender.amount === 0) continue;
      rows.push({
        key: `tender-${tender.bucket}`,
        label: `${bucketLabel(tender.bucket)} (${tender.count})`,
        value: money(tender.amount),
        barPct: barShare(tender.amount, tenders.collected),
      });
    }
    if (balanceTotal !== 0) {
      rows.push({
        key: 'balance-group',
        label: i18nT('v2Stats.dayClose.balanceGroup'),
        value: money(balanceTotal),
        muted: true,
        note: i18nT('v2Stats.dayClose.balanceGroupNote'),
      });
    }
    const compsTotal = roundPrice(
      tenders.comps.invitation.amount + tenders.comps.employee.amount
    );
    if (compsTotal !== 0) {
      rows.push({
        key: 'comps-group',
        label: i18nT('v2Stats.dayClose.compsGroup'),
        value: money(compsTotal),
        muted: true,
        note: i18nT('v2Stats.dayClose.compsGroupNote'),
      });
    }
    rows.push({
      key: 'collected',
      label: i18nT('v2Stats.dayClose.collectedSales'),
      value: money(tenders.collected),
      strong: true,
    });
    return {
      key: 'payments',
      title: i18nT('v2Stats.dayClose.sectionTenders'),
      rows,
    };
  });

  /**
   * Caja y efectivo: neutral session facts (no esperado/Faltaron/Sobraron
   * wording — that data lives only in the tech-detail card) + manual
   * movements + bridge sentences read verbatim from the payload.
   */
  const cajaSection = computed<IDayCloseReportSection | null>(() => {
    const p = payload.value;
    if (!p) return null;
    const collections = p.collections;
    const rows: IDayCloseReportRow[] = [];

    // Cobrado trail (mirrors the hero legend): Recibiste − Devoluciones =
    // Cobrado del día. Collapses to the bold row alone when there are no
    // refunds; on zero-sales days it only renders when money actually moved.
    const collected = getDayCloseCollectedDisplay(p);
    if (
      !zeroSales.value ||
      collected.collectedTotal !== 0 ||
      collected.refunds !== 0
    ) {
      if (collected.refunds !== 0) {
        rows.push({
          key: 'received',
          label: i18nT('v2Stats.dayClose.cajaReceived'),
          value: money(collected.received),
          note: i18nT('v2Stats.dayClose.cajaReceivedNote'),
        });
        rows.push({
          key: 'refunds',
          label: i18nT('v2Stats.dayClose.refunds'),
          value: `−${money(collected.refunds)}`,
        });
        rows.push({
          key: 'collected-total',
          label: i18nT('v2Stats.dayClose.cajaCollected'),
          value: money(collected.collectedTotal),
          strong: true,
        });
      } else {
        rows.push({
          key: 'collected-total',
          label: i18nT('v2Stats.dayClose.cajaCollected'),
          value: money(collected.collectedTotal),
          strong: true,
          note: i18nT('v2Stats.dayClose.cajaReceivedNote'),
        });
      }
    }

    const relevantSessions = p.cashSessions.filter(
      session =>
        session.state === 'closed' ||
        (session.state === 'open' && !session.spansMultipleDays)
    );
    relevantSessions.forEach((session, index) => {
      const range =
        session.state === 'open'
          ? `${formatTime(session.openedAt)} – ${i18nT(
              'v2Stats.dayClose.sessionOpen'
            ).toLowerCase()}`
          : `${formatTime(session.openedAt)} – ${formatTime(session.closedAt)}`;
      // statusId (index fallback) disambiguates cajas with several
      // sessions on the same business day (e.g. split shifts).
      const sessionKey = `session-${session.cajaId}-${
        session.statusId ?? index
      }`;
      rows.push({
        key: sessionKey,
        label: `${session.cajaName} · ${range}`,
        subheader: true,
      });
      if (session.cashCounted != null) {
        rows.push({
          key: `${sessionKey}-cash`,
          label: i18nT('v2Stats.dayClose.cashOnClose'),
          value: money(session.cashCounted),
          indent: true,
        });
      }
    });

    if (collections.manualInflows !== 0) {
      rows.push({
        key: 'manual-in',
        label: i18nT('v2Stats.dayClose.manualDeposits'),
        value: money(collections.manualInflows),
      });
    }
    if (collections.manualOutflows !== 0) {
      rows.push({
        key: 'manual-out',
        label: i18nT('v2Stats.dayClose.manualWithdrawals'),
        value: `−${money(collections.manualOutflows)}`,
      });
    }

    const sentences: string[] = [];
    const otherDays = collections.otherDaysCollections ?? 0;
    if (otherDays > 0) {
      sentences.push(
        i18nT('v2Stats.dayClose.otherDaysCollected', {
          amount: money(otherDays),
        })
      );
    }
    const uncollected = collections.uncollectedSales ?? 0;
    if (uncollected > roundPrice(p.waterfall.dayTotal * 0.01)) {
      sentences.push(
        i18nT('v2Stats.dayClose.uncollectedBySessions', {
          amount: money(uncollected),
        })
      );
    }

    // Renders only with content (rows, sentences or the stale toggle) —
    // never an empty card.
    if (!rows.length && !sentences.length && !staleSessions.value.length) {
      return null;
    }

    return {
      key: 'caja',
      title: i18nT('v2Stats.dayClose.sectionCollections'),
      leadIn:
        zeroSales.value && (rows.length || sentences.length)
          ? i18nT('v2Stats.dayClose.cajaZeroSalesLead')
          : undefined,
      rows,
      sentences,
      note:
        collections.source === 'estimated'
          ? i18nT('v2Stats.dayClose.estimatedNote')
          : undefined,
    };
  });

  /** Lo más vendido: top-5 categories (by net) with bars + top products. */
  const topSellersSection = computed<IDayCloseReportSection | null>(() => {
    const p = payload.value;
    if (!p || zeroSales.value) return null;
    const rows: IDayCloseReportRow[] = [];

    // Select by net desc: the payload orders "Sin categoría" last regardless
    // of size, but it must compete on merit here.
    const topCategories = [...p.categories]
      .sort((a, b) => b.net - a.net)
      .slice(0, 5);
    const categoriesNetTotal = roundPrice(
      p.categories.reduce((sum, category) => sum + category.net, 0)
    );
    for (const category of topCategories) {
      // source disambiguates: reporting and menu category ids are
      // independent sequences that can collide in one mixed payload.
      rows.push({
        key: `category-${category.source}-${category.id ?? 'uncat'}`,
        label: category.name,
        value: money(category.net),
        barPct: barShare(category.net, categoriesNetTotal),
      });
    }

    const topProducts = p.topProducts ?? [];
    if (topProducts.length) {
      if (rows.length) {
        rows.push({
          key: 'products-header',
          label: i18nT('v2Stats.dayClose.topProducts'),
          subheader: true,
        });
      }
      for (const product of topProducts) {
        rows.push({
          key: `product-${product.itemId ?? 'manual'}`,
          label: `${product.quantity}× ${
            product.itemId === null
              ? i18nT('v2Stats.dayClose.manualItems')
              : product.name
          }`,
          value: money(product.amount),
        });
      }
    }

    if (!rows.length) return null;
    return {
      key: 'top-sellers',
      title: i18nT('v2Stats.dayClose.sectionCategories'),
      rows,
    };
  });

  /** Club: top-ups, bonus, balance spent, points + legacy balance loads. */
  const clubSection = computed<IDayCloseReportSection | null>(() => {
    const p = payload.value;
    if (!p) return null;
    const club = p.club;
    const balanceLoads = p.nonSaleCollections.balanceLoads;
    if (
      club.topupsPrincipal === 0 &&
      club.topupsBonus === 0 &&
      club.balanceSpent === 0 &&
      club.pointsDiscounts === 0 &&
      balanceLoads === 0
    ) {
      return null;
    }
    const rows: IDayCloseReportRow[] = [];
    if (club.topupsPrincipal !== 0) {
      rows.push({
        key: 'topups',
        label: i18nT('v2Stats.dayClose.clubTopupsMoney'),
        value: money(club.topupsPrincipal),
      });
    }
    if (club.topupsBonus !== 0) {
      rows.push({
        key: 'bonus-gifted',
        label: i18nT('v2Stats.dayClose.clubBonusGifted'),
        value: money(club.topupsBonus),
        muted: true,
      });
    }
    if (club.balanceSpent !== 0) {
      rows.push({
        key: 'balance-spent',
        label: i18nT('v2Stats.dayClose.clubBalanceSpent'),
        value: money(club.balanceSpent),
      });
      if (club.bonusSpent !== 0) {
        rows.push({
          key: 'bonus-spent',
          label: i18nT('v2Stats.dayClose.clubBonusSpent'),
          value: money(club.bonusSpent),
          indent: true,
          muted: true,
        });
      }
    }
    if (club.pointsDiscounts !== 0) {
      rows.push({
        key: 'points',
        label: i18nT('v2Stats.dayClose.clubPointsDiscounts'),
        value: money(club.pointsDiscounts),
      });
    }
    if (balanceLoads !== 0) {
      rows.push({
        key: 'balance-loads',
        label: i18nT('v2Stats.dayClose.balanceLoadsRow'),
        value: money(balanceLoads),
      });
    }
    return {
      key: 'club',
      title: i18nT('v2Stats.dayClose.sectionClub'),
      rows,
    };
  });

  const hourlyRows = computed(() =>
    zeroSales.value ? [] : payload.value?.hourly ?? []
  );

  //#endregion

  //#region Lifecycle: init, branch switches, websocket refresh

  const wsEvent = computed(() => {
    const restaurant = restaurantStore.restaurant;
    return restaurant ? `r:${restaurant.id}:dclU` : null;
  });

  function onDayCloseWs(wsPayload: IDayCloseWsPayload) {
    if (wsPayload?.branchId !== restaurantStore.selectedBranch?.id) return;
    if (wsPayload.businessDate !== selectedDate.value) return;
    selectedVersion.value = null;
    load();
  }

  let boundWsEvent: string | null = null;

  function bindWs() {
    unbindWs();
    if (wsEvent.value && socketStore.socket) {
      socketStore.socket.on(wsEvent.value, onDayCloseWs);
      boundWsEvent = wsEvent.value;
    }
  }

  function unbindWs() {
    if (boundWsEvent && socketStore.socket) {
      socketStore.socket.off(boundWsEvent, onDayCloseWs);
    }
    boundWsEvent = null;
  }

  // Only the latest init run may proceed past its await (branch switches).
  let initGeneration = 0;

  async function init() {
    const generation = ++initGeneration;
    await loadContext();
    if (generation !== initGeneration) return;
    const queryDate = String(proxy.$route.query.date ?? '');
    const initial = /^\d{4}-\d{2}-\d{2}$/.test(queryDate)
      ? queryDate
      : currentBusinessDate.value;
    selectedDate.value =
      initial > currentBusinessDate.value ? currentBusinessDate.value : initial;
    selectedVersion.value = null;
    syncRouteQuery();
    bindWs();
    await load();
  }

  onMounted(init);
  onBeforeUnmount(unbindWs);

  watch(
    () => [
      restaurantStore.restaurant?.id ?? null,
      restaurantStore.selectedBranch?.id ?? null,
    ],
    () => {
      init();
    }
  );

  //#endregion
</script>

<template>
  <V2ReportPage
    :title="String($t('v2Stats.dayClose.title'))"
    :subtitle="restaurantStore.selectedBranch?.name"
  >
    <template #range>
      <div class="day-close-controls">
        <div class="date-nav">
          <PosUiButton
            color="neutral"
            variant="outline"
            size="md"
            icon="chevron-left"
            @click="selectDate(shiftDate(selectedDate, -1))"
          />
          <input
            class="date-input"
            type="date"
            :value="selectedDate"
            :max="currentBusinessDate"
            @change="onDateInputChange"
          />
          <PosUiButton
            color="neutral"
            variant="outline"
            size="md"
            icon="chevron-right"
            :disabled="isLiveDay"
            @click="selectDate(shiftDate(selectedDate, 1))"
          />
        </div>

        <select
          v-if="detail && detail.versions.length > 1"
          class="version-select"
          :value="payload?.header.version"
          @change="onVersionSelectChange"
        >
          <option
            v-for="meta in detail.versions"
            :key="meta.version"
            :value="meta.version"
          >
            {{ $t('v2Stats.dayClose.versionOption', { n: meta.version }) }}
          </option>
        </select>
      </div>
    </template>

    <template #actions>
      <PosUiButton
        v-if="detail && !detail.live"
        color="neutral"
        variant="outline"
        size="md"
        icon="refresh"
        :loading="regenerating"
        @click="regenerate"
      >
        <span class="action-label">{{
          $t('v2Stats.dayClose.regenerate')
        }}</span>
      </PosUiButton>
      <PosUiButton
        v-if="payload"
        color="primary"
        size="md"
        icon="printer"
        @click="print"
      >
        <span class="action-label">{{ $t('v2Stats.dayClose.print') }}</span>
      </PosUiButton>
    </template>

    <template #note>
      <div v-if="payload" class="day-close-badges">
        <PosUiBadge v-if="!detail?.live && payload.header.seq" color="neutral">
          Z{{ payload.header.seq }}
        </PosUiBadge>
        <PosUiBadge v-if="detail?.live" color="info">
          {{ $t('v2Stats.dayClose.liveBadge') }}
        </PosUiBadge>
        <PosUiBadge v-if="payload.header.version > 1" color="warning">
          {{
            $t('v2Stats.dayClose.amendedVersionBadge', {
              n: payload.header.version,
            })
          }}
        </PosUiBadge>
      </div>

      <div v-if="detail?.stale" class="stale-banner">
        <i class="stale-icon mdi mdi-alert-outline" />
        <span class="stale-text">{{ $t('v2Stats.dayClose.staleText') }}</span>
        <PosUiButton
          color="warning"
          variant="soft"
          size="sm"
          :loading="regenerating"
          @click="regenerate"
        >
          {{ $t('v2Stats.dayClose.regenerate') }}
        </PosUiButton>
      </div>
    </template>

    <!-- Loading skeleton -->
    <div v-if="loading" class="day-close-skeleton">
      <div class="skeleton-hero" />
      <div class="skeleton-kpis">
        <div v-for="n in 4" :key="n" class="skeleton-kpi" />
      </div>
      <div v-for="n in 3" :key="'card-' + n" class="skeleton-card" />
    </div>

    <!-- Missing snapshot (past day) -->
    <div v-else-if="notFound" class="day-close-empty">
      <PosUiEmptyState
        icon="file-search-outline"
        :title="String($t('v2Stats.dayClose.notFoundTitle'))"
        :description="String($t('v2Stats.dayClose.notFoundText'))"
      >
        <template #action>
          <PosUiButton
            color="primary"
            size="md"
            icon="file-plus-outline"
            :loading="regenerating"
            @click="regenerate"
          >
            {{ $t('v2Stats.dayClose.generate') }}
          </PosUiButton>
        </template>
      </PosUiEmptyState>
    </div>

    <template v-else-if="payload">
      <V2DayCloseAlerts v-if="alerts.length" :alerts="alerts" />

      <V2DayCloseHero :payload="payload" :currency-symbol="currencySymbol" />

      <V2DayCloseSectionCard v-if="salesSection" :section="salesSection" />
      <V2DayCloseSectionCard
        v-if="paymentsSection"
        :section="paymentsSection"
      />
      <V2DayCloseCajaCard
        v-if="cajaSection"
        :section="cajaSection"
        :stale-sessions="staleSessionRows"
      />
      <V2DayCloseSectionCard
        v-if="topSellersSection"
        :section="topSellersSection"
      />
      <V2DayCloseHourlyChart
        v-if="hourlyRows.length"
        :hours="hourlyRows"
        :currency-symbol="currencySymbol"
        :cutoff-hour="payload?.header.cutoffHour ?? 0"
      />
      <V2DayCloseSectionCard v-if="clubSection" :section="clubSection" />

      <V2DayCloseTechDetail
        :payload="payload"
        :currency-symbol="currencySymbol"
      />
    </template>
  </V2ReportPage>
</template>

<style scoped lang="scss">
  .day-close-controls {
    // Children carry tw-mb-2 (not the group) so internal wrap lines keep the
    // command bar's uniform rhythm (see V2ReportPage .report-controls).
    @apply tw-mr-3 tw-flex tw-flex-wrap tw-items-start;

    .date-nav {
      @apply tw-mb-2 tw-mr-2 tw-flex tw-items-center;

      .date-input {
        @apply tw-mx-1 tw-h-11 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-2.5 tw-text-sm tw-text-neutral-900;

        &:focus {
          @apply tw-border-orange-500 tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }
    }

    .version-select {
      @apply tw-mb-2 tw-h-11 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-2.5 tw-text-sm tw-text-neutral-900;

      &:focus {
        @apply tw-border-orange-500 tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
  }

  .day-close-badges {
    @apply tw-mb-3 tw-flex tw-flex-wrap tw-items-center;

    & > * {
      @apply tw-mb-1 tw-mr-1.5;
    }
  }

  .stale-banner {
    @apply tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-rounded-xl tw-border tw-border-solid tw-border-amber-300 tw-bg-amber-50 tw-px-4 tw-py-3;

    .stale-icon {
      @apply tw-mr-2 tw-text-lg tw-text-amber-600;
    }

    .stale-text {
      @apply tw-mr-3 tw-text-sm tw-text-amber-800;
    }
  }

  .day-close-skeleton {
    .skeleton-hero {
      @apply tw-mb-4 tw-h-32 tw-animate-pulse tw-rounded-xl tw-bg-neutral-200;
    }

    .skeleton-kpis {
      @apply tw-mb-4 tw-grid tw-grid-cols-2 tw-gap-3 lg:tw-grid-cols-4;

      .skeleton-kpi {
        @apply tw-h-24 tw-animate-pulse tw-rounded-xl tw-bg-neutral-200;
      }
    }

    .skeleton-card {
      @apply tw-mb-4 tw-h-48 tw-animate-pulse tw-rounded-xl tw-bg-neutral-200;
    }
  }

  .day-close-empty {
    @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white;
  }

  @media (max-width: 480px) {
    .action-label {
      @apply tw-hidden;
    }
  }
</style>
